import React, { Component, createRef, RefObject } from "react";
import { PlayerState } from "context/PlayerContext";

interface IAzurePlayerProps {
	source: string;
	duration: number;
	playerState: PlayerState;
	setPositionAudio: React.Dispatch<React.SetStateAction<number>>;
	setDurationAudio: React.Dispatch<React.SetStateAction<number>>;
	manualPosition: number | undefined;
}

export default class AndroidPlayer extends Component<IAzurePlayerProps> {
	audioNode: RefObject<HTMLAudioElement>;
	playPromise: Promise<void> | null = null; // Tracks the play promise

	constructor(props: IAzurePlayerProps) {
		super(props);
		this.audioNode = createRef();
	}

	componentWillUnmount() {
		if (this.audioNode.current) {
			this.audioNode.current.removeEventListener(
				"durationchange",
				this._updateDuration
			);
			this.audioNode.current.removeEventListener(
				"timeupdate",
				this._updatetime
			);
		}
	}

	componentDidMount() {
		if (this.audioNode.current) {
			this.audioNode.current.addEventListener(
				"timeupdate",
				this._updatetime.bind(this)
			);
			this.audioNode.current.addEventListener(
				"durationchange",
				this._updateDuration.bind(this)
			);
		}
		this._setAudio();
	}

	componentDidUpdate(prevProps: IAzurePlayerProps) {
		if (prevProps.playerState !== this.props.playerState) {
			if (this.props.playerState === PlayerState.playing) {
				this.playAudio();
			}
			if (this.props.playerState === PlayerState.paused) {
				this.pauseAudio();
			}
		}

		if (
			this.props.manualPosition !== undefined &&
			prevProps.manualPosition !== this.props.manualPosition
		) {
			this.seekToPosition(this.props.manualPosition);
		}

		if (prevProps.source !== this.props.source) {
			this._setAudio();
		}
	}

	/** Play function with error handling and promise tracking */
	playAudio = async () => {
		if (this.audioNode.current) {
			try {
				this.playPromise = this.audioNode.current.play();
				await this.playPromise;
				// console.log("Audio is playing...");
			} catch (e) {
				// console.error("Play error:", e);
				setTimeout(() => this.playAudio(), 500); // Retry after 500ms
			}
		}
	};

	/** Pause function that waits for play to resolve before pausing */
	pauseAudio = () => {
		if (this.audioNode.current) {
			if (this.playPromise) {
				this.playPromise
					.then(() => {
						if (this.audioNode.current) {
							this.audioNode.current.pause();
						}
					})
					.catch(() => {
						setTimeout(() => this.pauseAudio(), 500); // Retry pause after 500ms
					});
			} else {
				this.audioNode.current.pause();
			}
		}
	};

	/** Seek to a specific position in the audio */
	seekToPosition = (position: number) => {
		if (this.audioNode.current) {
			this.audioNode.current.pause();
			this.audioNode.current.currentTime = position;
			this.playAudio();
		}
	};

	_updatetime = () => {
		if (this.audioNode.current) {
			this.props.setPositionAudio(this.audioNode.current.currentTime);
		}
	};

	_updateDuration = () => {
		this.props.setDurationAudio(this.props.duration);
	};

	_isFirefox = () => {
		return /Firefox/i.test(navigator.userAgent);
	};

	_setAudio = () => {
		if (this.audioNode.current) {
			const audioSrc = this.props.source;
			this.audioNode.current.src = this._isFirefox()
				? audioSrc.replace("-OUTPUT", "").replace(".aac", ".mp3")
				: audioSrc;
		}
	};

	render() {
		return (
			<div>
				<audio ref={this.audioNode} />
			</div>
		);
	}
}
